/* eslint-disable unicorn/no-useless-promise-resolve-reject */

import { paths } from '@/config/paths';
import { useAuthStore } from '@/stores/authStore';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { queryClient } from './reactQuery';

// Types
export interface ApiResponse<T> {
  data: T;
}

export interface ApiErrorResponse {
  data: null;
  errors: {
    code: string;
    message?: string;
    details?: unknown;
  }[];
}

type ApiError = AxiosError<ApiErrorResponse>;

export const isApiError = (error: unknown): error is ApiError => {
  return error instanceof AxiosError && error.response?.data !== null;
};

// Client
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

// Interceptors
const onRequest = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  try {
    const session = await fetchAuthSession();
    if (session.tokens?.idToken) {
      config.headers.Authorization = `Bearer ${session.tokens.idToken.toString()}`;
    }
  } catch {
    return config;
  }
  return config;
};

const onResponseError = async (error: AxiosError): Promise<AxiosError> => {
  if (!isApiError(error) || error.response?.status !== 401) {
    return Promise.reject(error);
  }

  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

  if (!originalRequest._retry) {
    originalRequest._retry = true;

    try {
      const refreshedSession = await fetchAuthSession({ forceRefresh: true });

      if (refreshedSession.tokens?.idToken) {
        originalRequest.headers.Authorization = `Bearer ${refreshedSession.tokens.idToken.toString()}`;
        return apiClient(originalRequest);
      }
    } catch {
      await signOut();
      useAuthStore.getState().logout();
      queryClient.clear();
      localStorage.clear();
      globalThis.location.href = `${paths.auth.login.path}?refresh_error=true`;

      return Promise.reject(error);
    }
  }

  await signOut();
  useAuthStore.getState().logout();
  queryClient.clear();
  localStorage.clear();
  globalThis.location.href = `${paths.auth.login.path}?refresh_unsuccessful=true`;

  return Promise.reject(error);
};

apiClient.interceptors.request.use(onRequest);
apiClient.interceptors.response.use((res) => res, onResponseError);
